/* ═══════════════════════════════════════════════════════════════════════
   FizzyIQ — Brain Buzz / Stimulants vs FizzyFocus energy curve.

   Ported from the design handoff (`stimulants-chart.html`). One IIFE
   runs on every `.fizzy-brain-buzz` section on the page — each
   section gets its own closure so multiple instances would not share
   playback state. (In practice we only mount one per PDP, but the
   per-instance closure keeps the contract obvious.)

   Key logic notes:
     • SVG uses preserveAspectRatio="none". To position the dots in
       sync with the cursor's horizontal position, we sample each
       <path> at 600 evenly-spaced arc-length points and look up y
       at any cursor x via linear interpolation. The original
       prototype's arc-length-t approach drifted off-x on the wiggly
       stimulant curve — don't go back to that.
     • Autoplay is gated by IntersectionObserver at threshold 0.4 so
       the chart doesn't animate offscreen or replay endlessly.
     • prefers-reduced-motion short-circuits autoplay; the chart sits
       at the initial 10:30 am state and supports scrub immediately.
   ═══════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var SVG_VB_W = 800;
  var SVG_VB_H = 320;
  var PLAY_DURATION_MS = 7800;
  var INITIAL_T = 0.2083; // ≈ 10:30 am

  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(section) {
    /* Per-instance DOM refs — all queries scoped to `section`. */
    var stimPath  = section.querySelector('[data-bb-stim-path]');
    var fizzyPath = section.querySelector('[data-bb-fizzy-path]');
    var svgWrap   = section.querySelector('[data-bb-svg-wrap]');
    var cursor    = section.querySelector('[data-bb-cursor]');
    var dotStim   = section.querySelector('[data-bb-dot-stim]');
    var dotFizzy  = section.querySelector('[data-bb-dot-fizzy]');
    var nowEl     = section.querySelector('[data-bb-now]');
    var stimVal   = section.querySelector('[data-bb-stim-val]');
    var fizzyVal  = section.querySelector('[data-bb-fizzy-val]');
    var pinPeak1  = section.querySelector('[data-bb-pin="peak1"]');
    var pinCrash  = section.querySelector('[data-bb-pin="crash"]');
    var pinPeak2  = section.querySelector('[data-bb-pin="peak2"]');
    var replayBtn = section.querySelector('[data-bb-replay]');
    var pairingBtn = section.querySelector('[data-bb-pairing-toggle]');
    var pairingPop = section.querySelector('[data-bb-pairing-pop]');

    if (!stimPath || !fizzyPath || !svgWrap) return;

    /* ── x-indexed lookup tables ──
       Walk each path at evenly-spaced arc-length samples, then look
       up y at any cursor x via linear interpolation between the two
       bracketing samples. Faster than getPointAtLength on every
       frame, and accurate for the curves' x-monotonic regions. */
    function buildXLookup(path, samples) {
      var n = samples || 600;
      var len = path.getTotalLength();
      var pts = new Array(n + 1);
      for (var i = 0; i <= n; i++) {
        var p = path.getPointAtLength(len * i / n);
        pts[i] = { x: p.x, y: p.y };
      }
      return pts;
    }
    function yAtX(table, x) {
      if (x <= table[0].x) return table[0].y;
      if (x >= table[table.length - 1].x) return table[table.length - 1].y;
      /* Linear scan — n=600 means worst case is fast enough. */
      for (var i = 1; i < table.length; i++) {
        if (table[i].x >= x) {
          var a = table[i - 1], b = table[i];
          var k = (x - a.x) / (b.x - a.x || 1);
          return a.y + (b.y - a.y) * k;
        }
      }
      return table[table.length - 1].y;
    }

    var stimXY  = buildXLookup(stimPath, 600);
    var fizzyXY = buildXLookup(fizzyPath, 600);

    /* Map a viewBox y into a CSS top% inside .bb-svg-wrap. Because
       preserveAspectRatio="none" stretches the SVG to fill the
       wrapper, viewBox-y/viewBox-height = wrapper-top%. */
    function vbYToTopPct(y) { return (y / SVG_VB_H) * 100; }

    /* 5-bucket label function — name the energy state from viewBox y.
       Higher y = lower energy (SVG y-down). */
    function labelAtY(y) {
      if (y < 70)  return 'Peak';
      if (y < 130) return 'High';
      if (y < 200) return 'Steady';
      if (y < 260) return 'Dipping';
      return 'Crash';
    }

    /* t (0..1) → "h:mm am/pm" mapped over the 8am → 8pm window. */
    function clockFromT(t) {
      var minutes = t * 12 * 60;
      var totalMin = 8 * 60 + minutes;
      var hh = Math.floor(totalMin / 60);
      var mm = Math.floor(totalMin % 60);
      var ampm = hh >= 12 ? 'pm' : 'am';
      var hh12 = hh > 12 ? hh - 12 : (hh === 0 ? 12 : hh);
      var mmStr = mm < 10 ? '0' + mm : '' + mm;
      return hh12 + ':' + mmStr + ' ' + ampm;
    }

    function update(t) {
      t = Math.max(0, Math.min(1, t));
      var x = t * SVG_VB_W;
      var stimY  = yAtX(stimXY,  x);
      var fizzyY = yAtX(fizzyXY, x);

      var pct = t * 100;
      if (cursor)   cursor.style.left = pct + '%';
      if (dotStim)  { dotStim.style.left  = pct + '%'; dotStim.style.top  = vbYToTopPct(stimY)  + '%'; }
      if (dotFizzy) { dotFizzy.style.left = pct + '%'; dotFizzy.style.top = vbYToTopPct(fizzyY) + '%'; }

      if (nowEl)    nowEl.textContent    = clockFromT(t);
      if (stimVal)  stimVal.textContent  = labelAtY(stimY);
      if (fizzyVal) fizzyVal.textContent = labelAtY(fizzyY);
    }

    /* ── Anchor the annotation pins to the actual curve y at each
       event's x. Done once on init since the curve is static. ── */
    function placePin(pinEl, t) {
      if (!pinEl) return;
      var x = t * SVG_VB_W;
      var y = yAtX(stimXY, x);
      pinEl.style.left = (t * 100) + '%';
      pinEl.style.top  = vbYToTopPct(y) + '%';
    }
    placePin(pinPeak1, 2 / 12);  // 10 am
    placePin(pinCrash, 5 / 12);  //  1 pm
    placePin(pinPeak2, 8 / 12);  //  4 pm

    /* ── Scrub interactions ──
       Drag (mouse + touch) cancels any running animation. Hover
       free-scrubs only after the first autoplay has completed so
       the intro animation isn't preempted. */
    var dragging = false;
    var animId = null;
    var autoPlayed = false;

    function pctFromEvent(e) {
      var rect = svgWrap.getBoundingClientRect();
      var clientX;
      if (e.touches && e.touches[0]) clientX = e.touches[0].clientX;
      else if (e.clientX != null)    clientX = e.clientX;
      else return 0;
      return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    }

    svgWrap.addEventListener('mousedown', function (e) {
      dragging = true;
      if (animId) cancelAnimationFrame(animId);
      update(pctFromEvent(e));
    });
    window.addEventListener('mousemove', function (e) {
      if (dragging) update(pctFromEvent(e));
    });
    window.addEventListener('mouseup', function () { dragging = false; });

    svgWrap.addEventListener('mousemove', function (e) {
      if (!autoPlayed || dragging) return;
      update(pctFromEvent(e));
    });

    /* Touch scrub — mirror the mouse handlers. The handoff prototype
       did not bind these; adding them here so mobile can drag the
       chart too (passive: false so we can preventDefault on the
       gesture and stop the page from scrolling under the finger). */
    svgWrap.addEventListener('touchstart', function (e) {
      dragging = true;
      if (animId) cancelAnimationFrame(animId);
      update(pctFromEvent(e));
    }, { passive: true });
    svgWrap.addEventListener('touchmove', function (e) {
      if (!dragging) return;
      update(pctFromEvent(e));
    }, { passive: true });
    svgWrap.addEventListener('touchend',    function () { dragging = false; });
    svgWrap.addEventListener('touchcancel', function () { dragging = false; });

    /* ── Autoplay ── */
    function clearPins() {
      if (pinPeak1) pinPeak1.classList.remove('is-on');
      if (pinCrash) pinCrash.classList.remove('is-on');
      if (pinPeak2) pinPeak2.classList.remove('is-on');
    }
    function playFromZero() {
      if (animId) cancelAnimationFrame(animId);
      var start = performance.now();
      function step(now) {
        var t = Math.min(1, (now - start) / PLAY_DURATION_MS);
        update(t);
        if (pinPeak1 && t >= 2 / 12) pinPeak1.classList.add('is-on');
        if (pinCrash && t >= 5 / 12) pinCrash.classList.add('is-on');
        if (pinPeak2 && t >= 8 / 12) pinPeak2.classList.add('is-on');
        if (t < 1) animId = requestAnimationFrame(step);
      }
      animId = requestAnimationFrame(step);
      autoPlayed = true;
    }

    if (replayBtn) {
      replayBtn.addEventListener('click', function () {
        clearPins();
        if (prefersReducedMotion) {
          /* No animation when motion is reduced — just snap to the
             end state with all pins visible so users still get the
             same information density. */
          update(1);
          if (pinPeak1) pinPeak1.classList.add('is-on');
          if (pinCrash) pinCrash.classList.add('is-on');
          if (pinPeak2) pinPeak2.classList.add('is-on');
          autoPlayed = true;
          return;
        }
        playFromZero();
      });
    }

    /* Initial state — 10:30 am so the chart isn't blank on first
       paint. Autoplay (below) replays from t=0 once visible. */
    update(INITIAL_T);

    /* IO autoplay — first time the chart enters the viewport.
       Skipped under prefers-reduced-motion; the user can still
       scrub or click Replay to see the full curve. */
    if (prefersReducedMotion) {
      autoPlayed = true;
    } else if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !autoPlayed) {
            playFromZero();
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.4 });
      io.observe(svgWrap);
    } else {
      /* Old browser — just play immediately so the chart isn't
         frozen at the initial state forever. */
      playFromZero();
    }

    /* ── Pairing popover ── */
    if (pairingBtn && pairingPop) {
      function setOpen(open) {
        pairingBtn.classList.toggle('is-open', open);
        pairingPop.classList.toggle('is-on', open);
        pairingBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      pairingBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        setOpen(!pairingPop.classList.contains('is-on'));
      });
      document.addEventListener('click', function (e) {
        if (!pairingPop.contains(e.target) && e.target !== pairingBtn) {
          setOpen(false);
        }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') setOpen(false);
      });
    }
  }

  /* Defer until DOM is ready, then init every Brain Buzz section on
     the page. */
  function boot() {
    var sections = document.querySelectorAll('.fizzy-brain-buzz');
    if (!sections.length) return;
    sections.forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
